import { Marquee, ScrollReveal, SplitReveal } from '@/components/motion';
import { EXTERNAL_ASSESSORS, FACULTY, FACULTY_REGIONS, type FacultyMember } from '@/data/about';

/** Typographic initials for a monogram avatar — honorifics ("Dr.") stripped. */
function initialsOf(name: string): string {
  return name
    .replace(/\b(dr|prof|mr|mrs|ms|jr)\.?/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * InternationalFaculty (/about — section 3) — "Educators from across the world."
 *
 * A qualitative introduction to the teaching body: no head-count is stated
 * anywhere (the source figure is unverified per the sourcing note in
 * '@/data/about'), only the roster and the regions it spans. FACULTY_REGIONS
 * drives a slow, pause-on-hover Marquee of region chips; FACULTY renders as a
 * dense, ScrollReveal-staggered grid of compact plain hover-lift cards — a
 * typographic monogram avatar (no stock portraits), name and credential
 * snippet. A small closing block lists the two EXTERNAL_ASSESSORS.
 *
 * Motion: SplitReveal headline, ScrollReveal lede and grid staggers, Marquee
 * for the region ticker — reduced-motion safe throughout (the marquee falls
 * back to a static, scrollable row; nothing is ever stranded hidden).
 *
 * Sourcing: every name, region and credential comes from '@/data/about'
 * (source: /about/international-faculty/); nothing is invented or embellished.
 */
export function InternationalFaculty() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            International faculty
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Educators from across the world.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Our faculty bring an international mix of academic and industry backgrounds into
              the classroom, spanning the regions below.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Regions ticker ───────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.15}>
          <Marquee className="mt-10" speed={40} gap="gap-4">
            {FACULTY_REGIONS.map((region) => (
              <span
                key={region}
                className="inline-flex shrink-0 items-center rounded-full border border-border bg-muted/30 px-4 py-2 text-sm font-medium text-foreground"
              >
                {region}
              </span>
            ))}
          </Marquee>
        </ScrollReveal>

        {/* ── Faculty grid — compact plain hover-lift cards ──────────────── */}
        <ScrollReveal
          stagger={0.03}
          className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {FACULTY.map((member: FacultyMember) => (
            <div
              key={member.name}
              className="h-full rounded-2xl border border-border bg-background p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex items-start gap-4">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 font-display text-sm font-semibold tracking-wide text-brand-navy"
                >
                  {initialsOf(member.name)}
                </span>
                <div className="min-w-0">
                  <p className="font-display text-base leading-tight font-semibold text-foreground">
                    {member.name}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {member.detail}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </ScrollReveal>

        {/* ── External assessors ───────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            External assessors
          </p>
          <ScrollReveal
            stagger={0.08}
            className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2"
          >
            {EXTERNAL_ASSESSORS.map((assessor) => (
              <div
                key={assessor.name}
                className="flex items-start gap-4 rounded-2xl border border-border bg-muted/30 p-6"
              >
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 font-display text-sm font-semibold tracking-wide text-brand-navy"
                >
                  {initialsOf(assessor.name)}
                </span>
                <div className="min-w-0">
                  <p className="font-display text-base leading-tight font-semibold text-foreground">
                    {assessor.name}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {assessor.detail}
                  </p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
